import { selectUserContext } from 'core/userContext';
import { CreateCampaignContextProvider } from 'modules/campaignIndex/campaignFolders/contexts/CreateCampaignProvider';
import { FC, PropsWithChildren, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { useDebouncedState } from 'utils/hooks/useDebouncedState';
import { useUserTracking } from 'utils/hooks/useUserTracking';
import { NavigateState, PageState } from '../types';
import { QuickActionsMenuContext } from './QuickActionsMenuContext';

export const QuickActionsMenuContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const history = useHistory();
  const { track } = useUserTracking();
  const { orgId, project } = useSelector(selectUserContext);
  const [debouncedSearch, searchTerm, setSearchTerm] = useDebouncedState('', 300);
  const [pageState, setPageState] = useState<PageState | null>(null);
  const [value, setValue] = useState('');

  const trackPayload = {
    orgId: orgId || undefined,
    projectId: project?.id,
  };

  const selectPage = (state: PageState) => {
    setPageState(state);
    setSearchTerm('');
    setValue('');

    try {
      const action = state?.previousPage ? 'viewed' : 'opened';
      const message = `Quick Actions ${action}: ${state.page}`;
      track(message, trackPayload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error tracking Quick Actions', error);
    }
  };

  const closeMenu = () => {
    setSearchTerm('');
    setValue('');
    setPageState(null);
  };

  const navigate = (state: NavigateState) => {
    try {
      const message = `Quick Actions navigated: ${state.pageName}`;
      track(message, trackPayload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error tracking Quick Actions navigation', error);
    }

    if (state?.isExternal) {
      // handle Angular links
      window.location.assign(state.path);
    } else {
      // navigate to Angular page
      history.push(state.path);
    }
    closeMenu();
  };

  return (
    <CreateCampaignContextProvider>
      <QuickActionsMenuContext.Provider
        value={{
          closeMenu,
          debouncedSearchTerm: debouncedSearch.toLowerCase(),
          navigate,
          pageState,
          searchTerm,
          selectPage,
          setSearchTerm,
          setValue,
          value,
        }}
      >
        {children}
      </QuickActionsMenuContext.Provider>
    </CreateCampaignContextProvider>
  );
};
